import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Divider,
  LinearProgress,
  Button,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  styled,
  useTheme,
  Tooltip,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  Storage as StorageIcon,
  MoreVert as MoreVertIcon,
  PieChart as PieChartIcon,
  FilePresent as FilePresentIcon,
  Image as ImageIcon,
  VideoLibrary as VideoLibraryIcon,
  AudioFile as AudioFileIcon,
  Description as DocumentIcon,
  Archive as ArchiveIcon,
  CloudUpload as CloudUploadIcon,
  DeleteOutline as DeleteOutlineIcon,
  Upgrade as UpgradeIcon,
} from '@mui/icons-material';
import { RootState } from '../../store';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ChartTooltip } from 'recharts';
import dashboardService, { StorageStats } from '../../services/dashboardService';

// Styled components
const StorageTypeBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
  '&:last-child': {
    marginBottom: 0,
  },
}));

const ColorIndicator = styled(Box)<{ color: string }>(({ theme, color }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: color,
  marginRight: theme.spacing(1),
}));

// Storage overview component
const StorageOverview: React.FC = () => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<StorageStats | null>(null);
  
  // Fetch storage stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await dashboardService.getStorageStats();
        setStats(data);
        setError(null);
      } catch (err) {
        setError('Failed to load storage statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    // Refresh stats every 5 minutes
    const interval = setInterval(fetchStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Format bytes to readable format
  const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  // For pie chart hover interactions
  const onPieEnter = (_: any, index: number) => {
    setActiveIndex(index);
  };
  
  const onPieLeave = () => {
    setActiveIndex(null);
  };

  if (loading) {
    return (
      <Card elevation={0} sx={{ height: '100%', border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <StorageIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Storage Overview</Typography>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error || !stats) {
    return (
      <Card elevation={0} sx={{ height: '100%', border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <StorageIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Storage Overview</Typography>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <Typography color="error">{error || 'Failed to load storage statistics'}</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const usagePercentage = Math.round((stats.used / stats.total) * 100);
  
  // Storage type breakdown for pie chart
  const storageTypes = [
    { name: 'Documents', value: stats.byType.documents, color: theme.palette.primary.main, icon: <DocumentIcon /> },
    { name: 'Images', value: stats.byType.images, color: theme.palette.info.main, icon: <ImageIcon /> },
    { name: 'Videos', value: stats.byType.videos, color: theme.palette.error.main, icon: <VideoLibraryIcon /> },
    { name: 'Audio', value: stats.byType.audio, color: theme.palette.warning.main, icon: <AudioFileIcon /> },
    { name: 'Archives', value: stats.byType.archives, color: theme.palette.success.main, icon: <ArchiveIcon /> },
  ];

  // Calculate storage usage status color
  const getStatusColor = () => {
    if (usagePercentage < 70) return theme.palette.success.main;
    if (usagePercentage < 90) return theme.palette.warning.main;
    return theme.palette.error.main;
  };
  
  const statusColor = getStatusColor();

  return (
    <Card 
      elevation={0}
      sx={{ 
        height: '100%', 
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <StorageIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Storage Overview</Typography>
          </Box>
        }
        action={
          <IconButton onClick={handleMenuOpen}>
            <MoreVertIcon />
          </IconButton>
        }
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <PieChartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Storage Analytics</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <CloudUploadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cloud Backup</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cleanup Recommendations</ListItemText>
        </MenuItem>
      </Menu>
      <Divider />
      <CardContent>
        <Grid container spacing={2}>
          {/* Usage Overview */}
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Total Storage Usage
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h4" fontWeight={600}>
                {usagePercentage}%
              </Typography>
              <Typography variant="body1">
                {formatBytes(stats.used)} / {formatBytes(stats.total)}
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={usagePercentage} 
              sx={{ 
                height: 10, 
                borderRadius: 5,
                mb: 2,
                bgcolor: theme.palette.background.default,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: statusColor
                }
              }}
            />
            
            {usagePercentage > 80 && (
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  bgcolor: 'error.light', 
                  color: 'error.dark',
                  p: 1.5,
                  borderRadius: 1,
                  mb: 2
                }}
              >
                <Typography variant="body2" color="inherit">
                  Your storage is almost full. Consider upgrading your plan or cleaning up unused files.
                </Typography>
              </Box>
            )}
          </Grid>
          
          {/* Chart and Breakdown */}
          <Grid item xs={12}>
            <Box sx={{ height: 180, width: '100%', position: 'relative' }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={storageTypes}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                    onMouseEnter={onPieEnter}
                    onMouseLeave={onPieLeave}
                  >
                    {storageTypes.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.color}
                        stroke={activeIndex === index ? theme.palette.common.white : 'none'}
                        strokeWidth={activeIndex === index ? 2 : 0}
                      />
                    ))}
                  </Pie>
                  <ChartTooltip 
                    formatter={(value: number) => formatBytes(value)}
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 4,
                      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Grid>

          {/* Storage Type Breakdown */}
          <Grid item xs={12}>
            <Box sx={{ mt: 2 }}>
              {storageTypes.map((type, index) => (
                <StorageTypeBox key={type.name}>
                  <ColorIndicator color={type.color} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>{type.name}</span>
                      <span>{formatBytes(type.value)}</span>
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(type.value / stats.total) * 100}
                      sx={{
                        height: 4,
                        mt: 0.5,
                        bgcolor: alpha(type.color, 0.1),
                        '& .MuiLinearProgress-bar': {
                          bgcolor: type.color
                        }
                      }}
                    />
                  </Box>
                </StorageTypeBox>
              ))}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default StorageOverview; 